import { lazy, Suspense } from 'react'

import { Routes, Route } from 'react-router-dom'

import Typography from '@mui/material/Typography'

import BlankLayout from '@layouts/BlankLayout'
import { getServerMode, getSystemMode } from '@core/utils/serverHelpers'
import DashboardLayout from './DashboardLayout'

// Lazy load view components
const DashboardView = lazy(() => import('@/views/DashboardView'))
const UserListView = lazy(() => import('@/views/UserListView'))
const AddEditUserView = lazy(() => import('@/views/AddEditUserView'))
const BulkAddUserView = lazy(() => import('@/views/BulkAddUserView'))
const EditUserPage = lazy(() => import('@/views/EditUserPage'))
const TeamView = lazy(() => import('@/views/TeamView'))
const RolesAndPermissionsView = lazy(() => import('@/views/RolesAndPermissionsView'))
const RoleEditorView = lazy(() => import('@/views/RoleEditorView'))
const ApplicationView = lazy(() => import('@/views/ApplicationView'))
const AuditView = lazy(() => import('@/views/AuditView'))
const ConfigurationView = lazy(() => import('@/views/Configuration'))
const ExampleView = lazy(() => import('@/views/ExampleView'))
const TestView = lazy(() => import('@/views/TestView'))
const SetupView = lazy(() => import('@/views/SetupView'))
const NotFound = lazy(() => import('@/views/NotFound'))

// Permission-wrapped components
import usePermissionChecker from '@/SDK/usePermissionChecker'
import AccessLevel from '@/SDK/AccessLevel'

function PermissionGuard({
  permission,
  level,
  children
}: {
  permission: string
  level: AccessLevel
  children: React.ReactNode
}) {
  const { isLoading } = usePermissionChecker(permission, level)

  return isLoading ? null : <>{children}</>
}

// Example page wrapper (has extra Typography header)
const ExamplePage = () => (
  <>
    <Typography variant='h3' className='mb-2'>
      Permission Checking usage example
    </Typography>
    <ExampleView />
  </>
)

// Unauthorized page (uses BlankLayout)
const UnauthorizedPage = () => {
  const mode = getServerMode()
  const systemMode = getSystemMode()

  return (
    <BlankLayout systemMode={systemMode}>
      <NotFound mode={mode} type={401} />
    </BlankLayout>
  )
}

// Not found page (uses BlankLayout)
const NotFoundPage = () => {
  const mode = getServerMode()
  const systemMode = getSystemMode()

  return (
    <BlankLayout systemMode={systemMode}>
      <NotFound mode={mode} />
    </BlankLayout>
  )
}

const AppRoutes = () => (
  <Suspense fallback={<div>Loading...</div>}>
    <Routes>
      <Route element={<DashboardLayout />}>
        <Route index element={<DashboardView />} />
        <Route
          path='users'
          element={
            <PermissionGuard permission='Pages.Users' level={AccessLevel.READ}>
              <UserListView />
            </PermissionGuard>
          }
        />
        <Route
          path='users/add-user'
          element={
            <PermissionGuard permission='Pages.Users' level={AccessLevel.CREATE}>
              <AddEditUserView />
            </PermissionGuard>
          }
        />
        <Route
          path='users/bulk-add-user'
          element={
            <PermissionGuard permission='Pages.Users' level={AccessLevel.UPDATE}>
              <BulkAddUserView />
            </PermissionGuard>
          }
        />
        <Route
          path='users/detail'
          element={
            <PermissionGuard permission='Pages.Users' level={AccessLevel.READ}>
              <EditUserPage />
            </PermissionGuard>
          }
        />
        <Route
          path='teams'
          element={
            <PermissionGuard permission='Pages.Teams' level={AccessLevel.READ}>
              <TeamView />
            </PermissionGuard>
          }
        />
        <Route
          path='roles'
          element={
            <PermissionGuard permission='Pages.Roles and Permissions' level={AccessLevel.READ}>
              <RolesAndPermissionsView />
            </PermissionGuard>
          }
        />
        <Route
          path='roles/:appId'
          element={
            <PermissionGuard permission='Pages.Roles and Permissions' level={AccessLevel.READ}>
              <RoleEditorView />
            </PermissionGuard>
          }
        />
        <Route
          path='apps'
          element={
            <PermissionGuard permission='Pages.Application' level={AccessLevel.READ}>
              <ApplicationView />
            </PermissionGuard>
          }
        />
        <Route
          path='audit'
          element={
            <PermissionGuard permission='Pages.Audit Logs' level={AccessLevel.READ}>
              <AuditView />
            </PermissionGuard>
          }
        />
        <Route
          path='config'
          element={
            <PermissionGuard permission='Pages.Configuration' level={AccessLevel.READ}>
              <ConfigurationView />
            </PermissionGuard>
          }
        />
        <Route path='example' element={<ExamplePage />} />
        <Route
          path='test'
          element={
            <PermissionGuard permission='Pages.Roles and Permissions' level={AccessLevel.READ}>
              <TestView />
            </PermissionGuard>
          }
        />
      </Route>
      <Route path='setup' element={<SetupView />} />
      <Route path='401' element={<UnauthorizedPage />} />
      <Route path='*' element={<NotFoundPage />} />
    </Routes>
  </Suspense>
)

export default AppRoutes
